import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CreateExhibitionBulkItemResult,
  ExhibitionBulkResultStatus,
  ExhibitionConflictArtistData,
} from '../../../shared/dto/EntityRepository';
import { ExhibitionArtistResolutionReq } from '../../../shared/dto/ReqDtoRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../../../shared/component/headless/button/BtnBody';
import {
  ExhibitionArtistResolutionSelection,
  ExhibitionConflictChoice,
  ExhibitionConflictChoices,
  ExhibitionConflictField,
  buildResolveExhibitionConflictsReq,
  getExhibitionConflictFields,
} from '../../../shared/util/exhibitionBulk';
import { useAdminExhibition } from '../../share/hook/useAdminExhibition';

const STATUS_LABEL: Record<ExhibitionBulkResultStatus, string> = {
  CREATED: 'Created',
  SKIPPED_DUPLICATE: 'Skipped',
  CONFLICT: 'Conflict',
  INVALID: 'Invalid',
  UPDATED: 'Updated',
};

const FIELD_LABEL: Record<ExhibitionConflictField, string> = {
  title: 'Title',
  titleEn: 'English title',
  officialUrl: 'Official URL',
  sourceUrl: 'Source URL',
  artists: 'Artists',
};

const formatArtists = (artists: ExhibitionConflictArtistData[]) =>
  artists.length === 0
    ? 'No artists'
    : artists.map((artist) => (
      artist.nameEn ? `${artist.name} / ${artist.nameEn}` : artist.name
    )).join(', ');

const formatDiffValue = (
  field: ExhibitionConflictField,
  value: string | null | ExhibitionConflictArtistData[] | undefined,
) => {
  if (field === 'artists') {
    return formatArtists((value as ExhibitionConflictArtistData[] | undefined) ?? []);
  }
  if (value == null || value === '') return '—';
  return String(value);
};

const isExhibitionKeyConflict = (item: CreateExhibitionBulkItemResult) =>
  item.status === 'CONFLICT' && !!item.exhibitionConflict;

const isArtistConflict = (item: CreateExhibitionBulkItemResult) =>
  item.status === 'CONFLICT' && item.artistConflicts.length > 0;

type ResolutionByConflict = Record<string, ExhibitionArtistResolutionReq>;

const conflictKey = (rowNumber: number, artistIndex: number) =>
  `${rowNumber}:${artistIndex}`;

const ExhibitionBulkResult: React.FC = () => {
  const {
    bulkResult,
    bulkItems,
    setBulkResult,
    selectExhibitionById,
    retryBulkConflict,
    resolveBulkExhibitionConflicts,
  } = useAdminExhibition();
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [resolutions, setResolutions] = useState<ResolutionByConflict>({});
  const [fieldChoices, setFieldChoices] = useState<Record<number, ExhibitionConflictChoices>>({});
  const [retryingRows, setRetryingRows] = useState<ReadonlySet<number>>(new Set());
  const [applyingConflicts, setApplyingConflicts] = useState(false);
  const retryingRowsRef = useRef(new Set<number>());
  if (!bulkResult) return null;

  const rows = issuesOnly
    ? bulkResult.results.filter((item) => item.status === 'CONFLICT' || item.status === 'INVALID')
    : bulkResult.results;

  const chooseResolution = (
    rowNumber: number,
    artistIndex: number,
    resolution: ExhibitionArtistResolutionReq,
  ) => {
    setResolutions((current) => ({
      ...current,
      [conflictKey(rowNumber, artistIndex)]: resolution,
    }));
  };

  const chooseField = (
    rowNumber: number,
    field: ExhibitionConflictField,
    choice: ExhibitionConflictChoice,
  ) => {
    setFieldChoices((current) => ({
      ...current,
      [rowNumber]: {
        ...current[rowNumber],
        [field]: choice,
      },
    }));
  };

  const readyExhibitionConflicts = bulkResult.results.flatMap((item) => {
    if (!isExhibitionKeyConflict(item) || item.exhibitionId == null || !item.exhibitionConflict) {
      return [];
    }
    const fields = getExhibitionConflictFields(item.exhibitionConflict);
    const choices = fieldChoices[item.rowNumber] ?? {};
    if (fields.some((field) => !choices[field])) return [];
    const incoming = bulkItems.find((bulkItem) => bulkItem.rowNumber === item.rowNumber);
    if (!incoming) return [];
    return [{
      exhibitionId: item.exhibitionId,
      incoming: incoming.exhibition,
      conflict: item.exhibitionConflict,
      choices,
    }];
  });

  const applyExhibitionConflicts = async () => {
    if (readyExhibitionConflicts.length === 0 || applyingConflicts) return;
    setApplyingConflicts(true);
    try {
      await resolveBulkExhibitionConflicts(
        buildResolveExhibitionConflictsReq(readyExhibitionConflicts),
      );
      setFieldChoices((current) => {
        const next = { ...current };
        readyExhibitionConflicts.forEach((item) => {
          const row = bulkResult.results.find((result) =>
            result.exhibitionId === item.exhibitionId
            && result.exhibitionConflict,
          );
          if (row) delete next[row.rowNumber];
        });
        return next;
      });
    } finally {
      setApplyingConflicts(false);
    }
  };

  const retryRow = async (item: CreateExhibitionBulkItemResult) => {
    if (retryingRowsRef.current.has(item.rowNumber)) return;
    const selections: ExhibitionArtistResolutionSelection[] = item.artistConflicts.flatMap(
      ({ artistIndex }) => {
        const resolution = resolutions[conflictKey(item.rowNumber, artistIndex)];
        return resolution ? [{ artistIndex, resolution }] : [];
      },
    );
    if (selections.length !== item.artistConflicts.length) return;
    retryingRowsRef.current.add(item.rowNumber);
    setRetryingRows(new Set(retryingRowsRef.current));
    try {
      await retryBulkConflict(item.rowNumber, selections);
    } finally {
      retryingRowsRef.current.delete(item.rowNumber);
      setRetryingRows(new Set(retryingRowsRef.current));
    }
  };

  return (
    <ResultContainer>
      <Summary>
        {bulkResult.createdCount} created / {bulkResult.duplicateCount} skipped / {bulkResult.results.filter((item) => item.status === 'UPDATED').length} updated / {bulkResult.conflictCount} conflict / {bulkResult.invalidCount} error
      </Summary>
      <FilterBar>
        <HeadlessBtn
          value="All"
          handleClick={() => setIssuesOnly(false)}
          StyledBtn={!issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
        <HeadlessBtn
          value={`Issues (${bulkResult.conflictCount + bulkResult.invalidCount})`}
          handleClick={() => setIssuesOnly(true)}
          StyledBtn={issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
      </FilterBar>
      <ResultList>
        {rows.map((item) => {
          const allResolved = item.artistConflicts.every(({ artistIndex }) =>
            !!resolutions[conflictKey(item.rowNumber, artistIndex)]
          );
          const isRetrying = retryingRows.has(item.rowNumber);
          const openExhibition = () => {
            if (item.exhibitionId != null) void selectExhibitionById(item.exhibitionId);
          };
          return (
            <ResultRow
              key={item.rowNumber}
              $clickable={item.exhibitionId != null}
              role={item.exhibitionId != null ? 'button' : undefined}
              tabIndex={item.exhibitionId != null ? 0 : undefined}
              onClick={openExhibition}
              onKeyDown={(event) => {
                if (
                  event.target !== event.currentTarget
                  || (event.key !== 'Enter' && event.key !== ' ')
                ) return;
                event.preventDefault();
                openExhibition();
              }}
            >
              <RowMeta>Row {item.rowNumber}</RowMeta>
              <Status $issue={item.status === 'CONFLICT' || item.status === 'INVALID'}>
                {STATUS_LABEL[item.status]}
              </Status>
              {item.message && <Message>{item.message}</Message>}
              {isExhibitionKeyConflict(item) && item.exhibitionConflict && (
                <ConflictBox onClick={(event) => event.stopPropagation()}>
                  {getExhibitionConflictFields(item.exhibitionConflict).map((field) => {
                    const diff = item.exhibitionConflict?.[field];
                    if (!diff) return null;
                    const selected = fieldChoices[item.rowNumber]?.[field];
                    return (
                      <div key={field}>
                        <ConflictTitle>{FIELD_LABEL[field]}</ConflictTitle>
                        <CandidateList>
                          <CandidateButton
                            type="button"
                            $selected={selected === 'EXISTING'}
                            onClick={() => chooseField(item.rowNumber, field, 'EXISTING')}
                          >
                            <span>Keep current</span>
                            <small>{formatDiffValue(field, diff.existing)}</small>
                          </CandidateButton>
                          <CandidateButton
                            type="button"
                            $selected={selected === 'INCOMING'}
                            onClick={() => chooseField(item.rowNumber, field, 'INCOMING')}
                          >
                            <span>Use imported</span>
                            <small>{formatDiffValue(field, diff.incoming)}</small>
                          </CandidateButton>
                        </CandidateList>
                      </div>
                    );
                  })}
                </ConflictBox>
              )}
              {isArtistConflict(item) && item.artistConflicts.map((conflict) => {
                const selected = resolutions[conflictKey(item.rowNumber, conflict.artistIndex)];
                return (
                  <ConflictBox
                    key={conflict.artistIndex}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ConflictTitle>
                      Artist {conflict.artistIndex + 1} · artistIndex {conflict.artistIndex}: {conflict.inputArtist.name}
                    </ConflictTitle>
                    <InputMeta>
                      {[
                        conflict.inputArtist.nameEn,
                        conflict.inputArtist.instagramHandle
                          ? `@${conflict.inputArtist.instagramHandle}`
                          : '',
                        conflict.inputArtist.architraveHandle
                          ? `@${conflict.inputArtist.architraveHandle}`
                          : '',
                      ].filter(Boolean).join(' · ') || 'No additional information'}
                    </InputMeta>
                    <CandidateList>
                      {conflict.candidates.map((candidate) => {
                        const artistId = candidate.artistId;
                        if (artistId == null || artistId <= 0) return null;
                        return (
                        <CandidateButton
                          key={artistId}
                          type="button"
                          $selected={
                            selected?.resolutionType === 'LINK_EXISTING'
                            && selected.artistId === artistId
                          }
                          onClick={() => chooseResolution(
                            item.rowNumber,
                            conflict.artistIndex,
                            { resolutionType: 'LINK_EXISTING', artistId },
                          )}
                        >
                          <span>{candidate.name}{candidate.nameEn ? ` / ${candidate.nameEn}` : ''}</span>
                          <small>
                            {[
                              `ID ${candidate.artistId}`,
                              candidate.instagramHandle ? `IG @${candidate.instagramHandle}` : '',
                              candidate.architraveHandle ? `Architrave @${candidate.architraveHandle}` : '',
                            ].filter(Boolean).join(' · ')}
                          </small>
                        </CandidateButton>
                        );
                      })}
                      <CandidateButton
                        type="button"
                        $selected={selected?.resolutionType === 'CREATE_NEW'}
                        onClick={() => chooseResolution(
                          item.rowNumber,
                          conflict.artistIndex,
                          { resolutionType: 'CREATE_NEW', artistId: null },
                        )}
                      >
                        <span>Create a separate Artist</span>
                      </CandidateButton>
                    </CandidateList>
                  </ConflictBox>
                );
              })}
              {isArtistConflict(item) && (
                <RetryButtonRow onClick={(event) => event.stopPropagation()}>
                  <HeadlessBtn
                    value={isRetrying ? 'Retrying…' : 'Retry'}
                    handleClick={() => { void retryRow(item); }}
                    disabled={!allResolved || isRetrying}
                    StyledBtn={BlackFitBtn}
                  />
                </RetryButtonRow>
              )}
            </ResultRow>
          );
        })}
      </ResultList>
      <BtnContainer>
        {bulkResult.results.some(isExhibitionKeyConflict) && (
          <HeadlessBtn
            value={applyingConflicts
              ? 'Applying…'
              : `Apply selected (${readyExhibitionConflicts.length})`}
            handleClick={() => { void applyExhibitionConflicts(); }}
            disabled={readyExhibitionConflicts.length === 0 || applyingConflicts}
            StyledBtn={BlackFitBtn}
          />
        )}
        <HeadlessBtn
          value="Done"
          handleClick={() => setBulkResult(null)}
          StyledBtn={PaperBlurFitBtn}
        />
      </BtnContainer>
    </ResultContainer>
  );
};

const ResultContainer = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: 40px;
  &::-webkit-scrollbar { display: none; }
`;

const Summary = styled.div`
  padding: 8px 0 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const FilterBtnActive = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const FilterBtnInactive = styled(FilterBtnActive)`
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const ResultList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
`;

const ResultRow = styled.article<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-bottom: 10px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $issue: boolean }>`
  color: ${({ theme, $issue }) =>
    $issue ? theme.colors.color_Gray_02 : theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  word-break: break-word;
  ${({ theme }) => theme.typography.Body_04};
`;

const ConflictBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 0 3px;
`;

const ConflictTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  font-weight: 600;
`;

const InputMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_02};
`;

const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CandidateButton = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.color_Gray_06 : 'transparent'};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-align: left;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};

  small {
    color: ${({ theme }) => theme.colors.color_Gray_05};
  }
`;

const RetryButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 5px;
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

export default ExhibitionBulkResult;
